import React, { useState, useEffect, useCallback } from 'react';
import useDocumentTitle from '../../../hooks/useDocumentTitle/useDocumentTitle';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '../../../hooks/useMediaQuery/useMediaQuery';
import BackgroundImageContainer from '../../../components/ui/BackgroundImageContainer/BackgroundImageContainer';
import Container from '../../../components/ui/Container/Container';
import bg_image from "../../../assets/images/login-bg.jpg";
import Card, { CardAppearAnimationType } from '../../../components/ui/Card/Card';
import Button, { ButtonType } from '../../../components/ui/Button/Button';
import CopyRightFooter from '../../../components/ui/CopyRightFooter/CopyRightFooter';
import PhoneNumberInput from '../../../components/ui/PhoneNumberInput/PhoneNumberInput';
import { isMaskValueValid } from '../../../components/ui/PhoneNumberInput/utils';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faShieldAlt, faClock, faEnvelope, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import useSupplierGeographyApi from '../../../app/api/supplierGeography';
import { IUnsubscribeEmailNotificationPostAction } from '../../../app/interfaces/supplierGeography/IUnsubscribeEmailNotificationPostAction';
import { ResponseResultCode } from '../../../app/enums/ResponseResultCode';
import { toast } from 'react-toastify';
import ToastLayout from '../../../components/uiLayouts/ToastLayout/ToastLayout';
import { isAxiosError } from 'axios';


const UnsubscribeEmailNotificationPage: React.FC = () => {
    const { lang, id, hash } = useParams<{ lang: string, id: string, hash: string }>();

    // Передаем lang в настройки хука. 
    // Первый аргумент - пространство имен (null или 'translation'), второй - опции.
    const { t } = useTranslation(undefined, { lng: lang });

    const [email, setEmail] = useState('');
    const [unsubscribeError, setUnsubscribeError] = useState<boolean>(false);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        postUnsubscribeEmailNotificationData: {
            query: postUnsubscribeEmailNotification,
            cancel: cancelPostUnsubscribeEmailNotification,
            isLoading: postUnsubscribeEmailNotificationDataIsLoading
        }
    } = useSupplierGeographyApi();

    // Подтягиваем email из URL при загрузке компонента
    useEffect(() => {
        if (id) {
            try {
                // Декодируем Base64 обратно в текст
                const decodedEmail = atob(decodeURIComponent(id));
                setEmail(decodedEmail);
            } catch (e) {
                console.error("Invalid email format");
            }
        }
    }, [id]);

    // const validateEmail = (val: string) => {
    //     const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    //     return re.test(val.toLowerCase());
    // };

    // Основная функция отправки по аналогии с вашим примером
    const handleSubmit = useCallback(async (): Promise<void> => {
        //setEmailError(null);

        // Валидация перед отправкой
        // if (!email) {
        //     setEmailError("Email является обязательным полем.");
        //     return;
        // }

        // if (!validateEmail(email)) {
        //     setEmailError("Неверный формат email.");
        //     return;
        // }

        try {
            const body: IUnsubscribeEmailNotificationPostAction = {
                email: email.trim(),
                hash: hash
            };

            const res = await postUnsubscribeEmailNotification(body);

            // Обработка успешного ответа (как в вашем примере)
            if (res?.status === 200 && res.data.resultCode === ResponseResultCode.Ok) {
                setSubmissionSuccess(true);
            }
        } catch (err) {
            if (isAxiosError(err)) {
                if (err.response && err.response.data && err.response.status === 422 &&
                    err.response.data.resultCode === ResponseResultCode.NotValidData &&
                    err.response.data.errors && Array.isArray(err.response.data.errors)) {
                    setUnsubscribeError(true);
                }
            }
        }
    }, [email, postUnsubscribeEmailNotification]);

    return (
        <BackgroundImageContainer image={bg_image}>
            <Container centerContent={true} height={'calc(100% - 30px)'}>
                <Card
                    cardAppearAnimationType={CardAppearAnimationType.FadeIn}
                    style={{ width: '450px', maxWidth: 'calc(100% - 20px)' }}
                >
                    <div style={{ padding: '30px 20px' }}>
                        <h2 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '22px', color: '#333' }}>
                            {t("supplier-geography.unsubscribe.header")}
                        </h2>
                        {unsubscribeError ? (
                            <p style={{ color: '#FF4D4F', marginTop: '5px', fontSize: '13px' }}>{t("supplier-geography.unsubscribe.error")}</p>
                        ) : ("")}
                        {submissionSuccess ? (
                            /* --- НОВАЯ ФОРМА УСПЕШНОГО ВЫПОЛНЕНИЯ --- */
                            <div style={{ textAlign: 'center', animation: 'fadeIn 0.5s' }}>
                                <FontAwesomeIcon
                                    icon={faCheckCircle}
                                    style={{ color: '#28a745', fontSize: '48px', marginBottom: '20px' }}
                                />
                                <h2 style={{ textAlign: 'center', marginBottom: '15px', fontSize: '22px', color: '#333' }}>
                                    {t("supplier-geography.unsubscribe.success-header")}
                                </h2>
                                <p style={{ color: '#4E4E4E', marginBottom: '25px', fontSize: '16px', lineHeight: '1.5' }}>
                                    {t("supplier-geography.unsubscribe.success-message")}
                                </p>
                                <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                    <p style={{ fontSize: '14px', color: '#888' }}>
                                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '8px' }} />
                                        {t("supplier-geography.unsubscribe.success-info")}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p style={{ textAlign: 'left', marginBottom: '25px', fontSize: '15px', color: '#666', lineHeight: '1.4' }}>
                                    {t("supplier-geography.unsubscribe.description")}
                                </p>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ fontWeight: '600', marginBottom: '8px', display: 'block', fontSize: '14px' }}>
                                        {t("supplier-geography.unsubscribe.email")} *
                                    </label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type="email"
                                            value={email}
                                            // Убираем onChange, так как редактирование запрещено
                                            readOnly={true} // Делает поле нередактируемым
                                            placeholder="example@company.com"
                                            style={{
                                                width: '100%',
                                                padding: '12px 15px',
                                                borderRadius: '8px',
                                                border: `1px solid '#D9D9D9'`,
                                                fontSize: '16px',
                                                boxSizing: 'border-box',
                                                outline: 'none',
                                                transition: 'border-color 0.3s',
                                                // Добавляем стили для индикации неактивности:
                                                backgroundColor: '#f5f5f5', // Серый фон
                                                color: '#8c8c8c',           // Более бледный цвет текста
                                                cursor: 'not-allowed'       // Курсор "запрещено"
                                            }}
                                        />
                                    </div>
                                </div>

                                <div style={{
                                    backgroundColor: '#F5F7FA',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    marginBottom: '25px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}>
                                    <FontAwesomeIcon
                                        icon={faExclamationCircle} // Используем иконку восклицательного знака
                                        style={{ color: 'blue', marginRight: '12px', fontSize: '1.25em' }} // Цвет 'blue' или конкретный HEX-код
                                    />
                                    <p style={{ fontSize: '13px', color: '#555', margin: 0 }}>
                                        {t("supplier-geography.unsubscribe.info-message")}
                                    </p>
                                </div>

                                <Button
                                    buttonType={ButtonType.Primary}
                                    onClick={handleSubmit}
                                    style={{ width: '100%', padding: '12px', height: 'auto', fontSize: '16px' }}
                                    disabled={isLoading}
                                >
                                    {t("supplier-geography.unsubscribe.submit-button")}
                                </Button>

                                <div style={{ marginTop: '25px', borderTop: '1px solid #EEE', paddingTop: '15px' }}>
                                    <p style={{ fontSize: '12px', color: '#999', textAlign: 'center', marginBottom: '8px' }}>
                                        <FontAwesomeIcon icon={faShieldAlt} style={{ marginRight: '5px' }} />
                                        {t("supplier-geography.unsubscribe.security-note")}
                                    </p>
                                    <p style={{ fontSize: '12px', color: '#999', textAlign: 'center' }}>
                                        <FontAwesomeIcon icon={faClock} style={{ marginRight: '5px' }} />
                                        {t("supplier-geography.unsubscribe.timing-note")}
                                    </p>
                                </div>
                            </>
                        )}
                    </div>
                </Card>
            </Container>
            <CopyRightFooter />
        </BackgroundImageContainer>
    );
};

export default UnsubscribeEmailNotificationPage;


